/**
 * M27 AI photo-intake (#451) — entitlement, scan-credit balance and the beta flag.
 *
 * Pure, framework-free helpers (the project's Vitest is `node`-env with no jsdom, so
 * the decisions live here and the components stay dumb — same convention as
 * `scanFlow.js`). Everything below answers one question: **may this org start a scan
 * right now, and if not, which of the two "no" states are they in?**
 *
 * The four states map 1:1 onto what the intake surface renders:
 *
 *   `hidden`     — the beta flag is off. The photo door does not exist; the SPA shows
 *                  the same Add-Part step it showed before M27.
 *   `locked`     — flag on, plan not entitled (`ai_intake_enabled` false: Starter,
 *                  Trial, or a lapsed Shop/Pro). The `.pen` `lock-tile` → "Upgrade".
 *   `exhausted`  — flag on, entitled, zero credits left. The `.pen` quota-empty state:
 *                  amber "0 of N left" + a buy-more affordance. The scan cannot start.
 *   `available`  — flag on, entitled, credits left. The normal camera door, with the
 *                  balance shown so the cost is never a surprise.
 *
 * Both "no" states are the client-side mirrors of the backend's authoritative 403s
 * (`upgrade_required` / `quota_exceeded` — see `scanFlow.scanStartError`). The backend
 * remains the gate; this exists so the seller is told BEFORE they photograph a part
 * rather than after uploading. Fail CLOSED: a missing/malformed subscription reads as
 * `locked`, never as available.
 *
 * Balance figures come from `Subscription` on `/sellers/mine/`
 * (`ai_intake_enabled`, `ai_credits_included`, `ai_credits_purchased`,
 * `ai_credits_remaining`) — plan allowances are server-derived (`PLAN_AI_CREDITS`) and
 * are deliberately NOT duplicated in the bundle.
 */

/** The four render states, above. */
const PHOTO_INTAKE_STATE = {
  HIDDEN: 'hidden',
  LOCKED: 'locked',
  EXHAUSTED: 'exhausted',
  AVAILABLE: 'available',
};

/** Vite env var that opts a build into the photo-intake beta. */
const PHOTO_INTAKE_FLAG = 'VITE_FEATURE_PHOTO_INTAKE';

/** The plan every upsell in this feature points at — Shop is the cheapest entitled
 * plan (`Subscription.ai_intake_enabled` = Shop|Pro), which is why the `.pen` lock
 * tile reads "Upgrade to Shop" rather than naming Pro. */
const PHOTO_INTAKE_MIN_PLAN = 'Shop';

const TRUTHY = ['1', 'true', 'on', 'yes'];
const FALSY = ['0', 'false', 'off', 'no', '', 'none', 'null', 'undefined'];


/**
 * Is the photo-intake beta on for this build?
 *
 * An explicit flag value always wins in BOTH directions, so a production build can opt
 * in and a dev build can opt out. With no flag set the default is "on everywhere except
 * a production bundle" — M27 ships behind the flag with the Stub OCR provider (no live
 * OCR credentials exist yet), so a released build must not surface it by accident,
 * while dev and CI see the feature without ceremony.
 *
 * @param {{flag?: string|undefined, isProduction?: boolean}} [options]
 * @returns {boolean}
 */
function isPhotoIntakeBetaEnabled({ flag, isProduction = false } = {}) {
  const value = String(flag ?? '').trim().toLowerCase();
  if (TRUTHY.includes(value)) {
    return true;
  }
  if (flag !== undefined && flag !== null && FALSY.includes(value)) {
    return false;
  }
  return !isProduction;
}


/**
 * The build's beta state, read from Vite's env. Thin wrapper over the pure helper above
 * (which is what the tests cover) so nothing else in the app touches `import.meta.env`.
 *
 * @returns {boolean}
 */
function photoIntakeBetaEnabled() {
  return isPhotoIntakeBetaEnabled({
    flag: import.meta.env[PHOTO_INTAKE_FLAG],
    isProduction: import.meta.env.MODE === 'production',
  });
}


/** Coerce a serialized credit count to a non-negative integer. A missing or junk value
 * reads as 0 — the fail-closed direction for a balance. */
function creditCount(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
}


/**
 * Normalise the AI-credit block of a subscription payload.
 *
 * `total` is the denominator the balance copy needs — the plan allowance PLUS any
 * purchased packs, because a top-up raises the ceiling rather than being spent first
 * (`ai_credits_remaining = included + purchased - used`). It is floored at `remaining`
 * so a stale/partial payload can never render the nonsense "260 of 250 left".
 *
 * @param {object|null} subscription the `seller.subscription` payload.
 * @returns {{entitled: boolean, remaining: number, total: number, exhausted: boolean}}
 */
function scanCredits(subscription) {
  // Strict `=== true`, not a truthiness coercion: the entitlement is a serialized
  // boolean, so anything else is a malformed payload and must read as NOT entitled.
  const entitled = subscription?.ai_intake_enabled === true;
  const remaining = creditCount(subscription?.ai_credits_remaining);
  const allowance = creditCount(subscription?.ai_credits_included)
    + creditCount(subscription?.ai_credits_purchased);
  return {
    entitled,
    remaining,
    total: Math.max(allowance, remaining),
    exhausted: entitled && remaining < 1,
  };
}


/**
 * The balance line — the `.pen` "0 of 50 left" copy, with the real server-driven
 * numbers. Returns null for an unentitled org: there is no balance to quote when the
 * plan has no allowance at all (that org gets the lock tile instead).
 *
 * @param {object|null} subscription
 * @returns {string|null}
 */
function formatScanCredits(subscription) {
  const { entitled, remaining, total } = scanCredits(subscription);
  if (!entitled) {
    return null;
  }
  const unit = total === 1 ? 'scan' : 'scans';
  return `${remaining} of ${total} ${unit} left`;
}


/**
 * THE STATE SELECTOR — which of the four surfaces the seller gets.
 *
 * Order matters and is the product rule: the beta flag outranks everything (an org that
 * cannot see the feature is told nothing about plans), then the plan gate, then the
 * balance. An entitled-but-empty org is `exhausted`, NOT `locked`: they are on the right
 * plan and need credits, so pointing them at an upgrade would be wrong.
 *
 * @param {{subscription?: object|null, betaEnabled?: boolean, canWrite?: boolean}} options
 * @returns {string} a `PHOTO_INTAKE_STATE` value.
 */
function photoIntakeState({ subscription, betaEnabled = false, canWrite = true } = {}) {
  if (!betaEnabled || !canWrite) {
    return PHOTO_INTAKE_STATE.HIDDEN;
  }
  const { entitled, exhausted } = scanCredits(subscription);
  if (!entitled) {
    return PHOTO_INTAKE_STATE.LOCKED;
  }
  return exhausted ? PHOTO_INTAKE_STATE.EXHAUSTED : PHOTO_INTAKE_STATE.AVAILABLE;
}


/**
 * May a scan actually be STARTED? The one predicate the drawer/entry point gates its
 * write on, so "can I click Scan" is never re-derived from the state string.
 *
 * @param {string} state a `PHOTO_INTAKE_STATE` value.
 * @returns {boolean}
 */
function canStartScan(state) {
  return state === PHOTO_INTAKE_STATE.AVAILABLE;
}


/**
 * Copy for the two blocked states, shared by the intake tile and the drawer's own
 * error surface so a 403 that slips past the client gate reads identically to the
 * pre-emptive block.
 *
 * Keyed by the BACKEND's 403 codes (`upgrade_required` / `quota_exceeded`) as well as
 * the local state names, because both callers arrive with one or the other.
 *
 * @param {string} key a `PHOTO_INTAKE_STATE` value or a backend error code.
 * @returns {{title: string, message: string, cta: string|null}|null}
 */
function photoIntakeBlockCopy(key) {
  if (key === PHOTO_INTAKE_STATE.LOCKED || key === 'upgrade_required') {
    return {
      title: `Add via photo is on the ${PHOTO_INTAKE_MIN_PLAN} plan`,
      message: 'Photograph a part and we read the label, match the part number and prefill '
        + 'the draft for you to confirm.',
      cta: `Upgrade to ${PHOTO_INTAKE_MIN_PLAN}`,
    };
  }
  if (key === PHOTO_INTAKE_STATE.EXHAUSTED || key === 'quota_exceeded') {
    return {
      title: 'You\'re out of scans this month',
      message: 'Your allowance resets with your billing period. Add a part by hand in the '
        + 'meantime — nothing else is affected.',
      cta: 'Buy more scans',
    };
  }
  return null;
}


export {
  canStartScan,
  formatScanCredits,
  isPhotoIntakeBetaEnabled,
  PHOTO_INTAKE_FLAG,
  PHOTO_INTAKE_MIN_PLAN,
  PHOTO_INTAKE_STATE,
  photoIntakeBetaEnabled,
  photoIntakeBlockCopy,
  photoIntakeState,
  scanCredits,
};
